import { TEST_ID_CUSTOM_PROMPT } from "e2e/testId";
import React, { LegacyRef, memo } from "react";
import { Platform, TextInputProps } from "react-native";

import {
  AlertDialog,
  AlertDialogBackdrop,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
} from "@/components/atoms/AlertDialog";
// design-system: compound waiver — non-collapsible button composition
// eslint-disable-next-line no-restricted-imports -- RFC-#6 compound waiver: Button (transparent-bg footer buttons with ButtonText children) AND Input compound with a ref on InputField — non-collapsible…
import { Button, ButtonText } from "@/components/ui/button";
import { Heading } from "@/components/atoms/Heading";
// design-system: compound waiver — non-collapsible input composition
// eslint-disable-next-line no-restricted-imports -- RFC-#6 compound waiver: Button (transparent-bg footer buttons with ButtonText children) AND Input compound with a ref on InputField — non-collapsible…
import { Input, InputField } from "@/components/ui/input";
import { Spinner } from "@/components/atoms/Spinner";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import { ifWeb } from "@/utils/platform";

/**
 * Props for the CustomPromptView component
 */
interface Props {
  inputRef: LegacyRef<TextInputProps> | undefined;
  isOpen: boolean;
  title: string;
  message?: string;
  value: string;
  /** Whether the Save action is in progress */
  isLoading?: boolean;
  /** Error message to display below the input field */
  errorMessage?: string;
  onChangeText?: (text: string) => void;
  onDone?: () => void;
  onClose: () => void;
}

/**
 * Renders a modal prompt dialog with text input
 * @param root0 - The component props
 * @param root0.inputRef - Ref to attach to the text input field
 * @param root0.isOpen - Whether the prompt dialog is currently visible
 * @param root0.title - Prompt dialog title text
 * @param root0.message - Optional description or instructions for the prompt
 * @param root0.value - Current value of the input field
 * @param root0.isLoading - Whether the Save action is in progress
 * @param root0.errorMessage - Error message displayed below the input field
 * @param root0.onChangeText - Callback invoked when user types in the input field
 * @param root0.onClose - Callback invoked when dialog is dismissed
 * @param root0.onDone - Callback invoked when user confirms the input
 */
const CustomPromptView = ({
  inputRef,
  isOpen,
  title,
  message,
  value,
  isLoading,
  errorMessage,
  onChangeText,
  onClose,
  onDone,
}: Props) => (
  <AlertDialog
    isOpen={isOpen}
    onClose={isLoading ? undefined : onClose}
    useRNModal={Platform.OS !== "web"}
    size="sm"
  >
    <AlertDialogBackdrop className="bg-black" />
    <AlertDialogContent className="border-0 bg-surface-base p-0 dark:bg-prompt-background">
      <AlertDialogBody className="p-5 pb-4">
        <Stack space="6">
          <Stack space="1">
            <Heading
              variant="title"
              className="shrink text-center text-content-primary dark:text-white"
            >
              {title}
            </Heading>
            {!!message && (
              <Text
                variant="unstyled"
                className="shrink text-center font-medium text-title text-content-muted"
              >
                {message}
              </Text>
            )}
          </Stack>
          <Stack space="2">
            <Input
              variant="outline"
              className="h-12 rounded-sm border-outline-default bg-surface-raised dark:border-prompt-border dark:bg-surface-action"
              isDisabled={isLoading}
            >
              <InputField
                ref={inputRef}
                testID={TEST_ID_CUSTOM_PROMPT.INPUT}
                value={value}
                placeholder={title}
                autoCorrect={false}
                spellCheck={false}
                onChangeText={onChangeText}
                className="text-title text-content-primary dark:text-white"
              />
            </Input>
            {errorMessage ? (
              <Text
                variant="body"
                className="text-needs-semantic-error-600 dark:text-needs-semantic-error-400"
              >
                {errorMessage}
              </Text>
            ) : null}
          </Stack>
        </Stack>
      </AlertDialogBody>
      <AlertDialogFooter className="gap-0 border-t border-outline-default bg-surface-base dark:border-prompt-border dark:bg-prompt-background">
        <Button
          testID={TEST_ID_CUSTOM_PROMPT.CANCEL_BUTTON}
          onPress={onClose}
          isDisabled={isLoading}
          className="web:hover:!bg-surface-muted dark:web:hover:!bg-surface-inverse/10 h-14 flex-1 rounded-none border-r border-outline-default !bg-transparent active:!bg-surface-strong dark:border-prompt-border dark:active:!bg-surface-inverse/15"
        >
          <ButtonText className="text-title-lg !text-content-neutral-strong dark:!text-content-subtle">
            Cancel
          </ButtonText>
        </Button>
        <Button
          testID={TEST_ID_CUSTOM_PROMPT.SAVE_BUTTON}
          onPress={onDone}
          isDisabled={!value || isLoading}
          className="web:hover:!bg-surface-muted dark:web:hover:!bg-surface-inverse/10 h-14 flex-1 rounded-none !bg-transparent active:!bg-surface-strong dark:active:!bg-surface-inverse/15"
        >
          {isLoading ? (
            <Spinner size="small" />
          ) : (
            <ButtonText
              className={`${ifWeb("text-title", "text-title-lg")} font-heading ${value ? "!text-needs-semantic-primary-500 dark:!text-needs-semantic-primary-400" : "!text-content-subtle dark:!text-content-neutral-strong"}`}
            >
              Save
            </ButtonText>
          )}
        </Button>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

CustomPromptView.displayName = "CustomPromptView";

export default memo(CustomPromptView);
